/**
 * The billing models a plan can choose, and how each one is labelled.
 *
 * `perTenderAnalysis` and `perConcept` are the tender pricing meters (tri-server
 * `founder/tender-pricing-spec.md`, "Meters"). tri-model gains them in PR #34, which is not
 * published yet — so they are matched as string literals here. Once the portal is on that
 * version they can become `MeteringAlgorithm.perTenderAnalysis` / `.perConcept`.
 */
import { MeteringAlgorithm } from '@the-real-insight/tri-model';

/** The slice of i18next's `t` used here. The options-object form is the one react-i18next's
 *  overloaded `TFunction` is assignable to (a positional fallback string is not). */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export const PER_TENDER_ANALYSIS = 'perTenderAnalysis';
export const PER_CONCEPT = 'perConcept';

/** Billing models the plan editor offers, in this order, with the label key and English default. */
const BILLING_MODELS: { key: string; label: string; defaultValue?: string }[] = [
    { key: MeteringAlgorithm.perNamedUser, label: 'ui.namedUser' },
    { key: MeteringAlgorithm.perRequest, label: 'ui.perRequest' },
    { key: MeteringAlgorithm.perToken, label: 'ui.perToken', defaultValue: 'Per token' },
    { key: PER_TENDER_ANALYSIS, label: 'ui.perTenderAnalysis', defaultValue: 'Per analysed tender' },
    { key: PER_CONCEPT, label: 'ui.perConcept', defaultValue: 'Per concept' },
];

const billingModelText = (model: (typeof BILLING_MODELS)[number], t: Translate): string =>
    model.defaultValue === undefined ? t(model.label) : t(model.label, { defaultValue: model.defaultValue });

/** Billing models the plan editor offers, in this order. */
export function billingModelOptions(t: Translate): { key: string; text: string }[] {
    return BILLING_MODELS.map((model) => ({ key: model.key, text: billingModelText(model, t) }));
}

/**
 * The label of a plan's billing model. No algorithm is the named-user default; an algorithm this
 * portal does not know is shown as it came — never relabelled as "Named user".
 */
export function billingModelLabel(algorithm: string | undefined | null, t: Translate): string {
    if (!algorithm) return t('ui.namedUser');
    const model = BILLING_MODELS.find((candidate) => candidate.key === algorithm);
    return model ? billingModelText(model, t) : String(algorithm);
}

/** Usage meters — an included quantity plus a fee per unit beyond it, rather than seats. */
export function isUsageMeter(algorithm: string | undefined | null): boolean {
    return (
        algorithm === MeteringAlgorithm.perRequest ||
        algorithm === MeteringAlgorithm.perToken ||
        algorithm === PER_TENDER_ANALYSIS ||
        algorithm === PER_CONCEPT
    );
}

/**
 * The smallest included quantity a usage meter accepts. A tender plan may include none at all
 * (Radar: 0 concepts), the older meters keep their minimum of one.
 */
export function minimumIncluded(algorithm: string | undefined | null): number {
    return algorithm === PER_TENDER_ANALYSIS || algorithm === PER_CONCEPT ? 0 : 1;
}

/** Label of the "included" field for a usage meter. */
export function includedLabel(algorithm: string | undefined | null, t: Translate): string {
    switch (algorithm) {
        case MeteringAlgorithm.perToken:
            return t('ui.includedTokens', { defaultValue: 'Included tokens' });
        case PER_TENDER_ANALYSIS:
            return t('ui.includedTenderAnalyses', { defaultValue: 'Included analyses' });
        case PER_CONCEPT:
            return t('ui.includedConcepts', { defaultValue: 'Included concepts' });
        default:
            return t('ui.includedRequests');
    }
}

/** Label of the "fee per additional unit" field for a usage meter. */
export function additionalFeeLabel(algorithm: string | undefined | null, t: Translate): string {
    switch (algorithm) {
        case MeteringAlgorithm.perToken:
            return t('ui.feePerAdditionalToken', { defaultValue: 'Fee per additional token' });
        case PER_TENDER_ANALYSIS:
            return t('ui.feePerAdditionalTenderAnalysis', { defaultValue: 'Fee per additional analysis' });
        case PER_CONCEPT:
            return t('ui.feePerAdditionalConcept', { defaultValue: 'Fee per additional concept' });
        default:
            return t('ui.feePerAdditionalRequest');
    }
}

/**
 * The unit's name in the sign-up page's plan summary ("100 Analysen enthalten, 7.00 € pro weitere
 * Analysen"). German only, like the rest of that summary; a billing's own name wins.
 */
export function billingUnitName(algorithm: string, billingName?: string): string {
    if (billingName) {
        return billingName;
    }
    switch (algorithm) {
        case 'perRequest':
            return 'Anfragen';
        case 'perToken':
            return 'Tokens';
        case 'perData':
            return 'Data';
        case PER_TENDER_ANALYSIS:
            return 'Analysen';
        case PER_CONCEPT:
            return 'Konzepte';
        default:
            return algorithm;
    }
}
